'use client'

import { useCallback, useEffect, useState } from 'react'
import {
  ACCESSIBILITY_STORAGE_KEY,
  CONTRAST_MODES,
  DEFAULT_ACCESSIBILITY_SETTINGS,
  MOTION_MODES,
  TEXT_SIZES,
  type AccessibilitySettings,
  type ContrastMode,
  type MotionMode,
  type TextSize,
} from './accessibility-settings-shared'
export {
  ACCESSIBILITY_STORAGE_KEY,
  CONTRAST_MODES,
  DEFAULT_ACCESSIBILITY_SETTINGS,
  MOTION_MODES,
  TEXT_SIZES,
  type AccessibilitySettings,
  type ContrastMode,
  type MotionMode,
  type TextSize,
} from './accessibility-settings-shared'

function isTextSize(value: unknown): value is TextSize {
  return typeof value === 'string' && (TEXT_SIZES as readonly string[]).includes(value)
}

function isContrastMode(value: unknown): value is ContrastMode {
  return typeof value === 'string' && (CONTRAST_MODES as readonly string[]).includes(value)
}

function isMotionMode(value: unknown): value is MotionMode {
  return typeof value === 'string' && (MOTION_MODES as readonly string[]).includes(value)
}

export function parseAccessibilitySettings(raw: unknown): AccessibilitySettings {
  if (!raw || typeof raw !== 'object') return DEFAULT_ACCESSIBILITY_SETTINGS
  const candidate = raw as Partial<Record<keyof AccessibilitySettings, unknown>>
  return {
    textSize: isTextSize(candidate.textSize)
      ? candidate.textSize
      : DEFAULT_ACCESSIBILITY_SETTINGS.textSize,
    contrast: isContrastMode(candidate.contrast)
      ? candidate.contrast
      : DEFAULT_ACCESSIBILITY_SETTINGS.contrast,
    motion: isMotionMode(candidate.motion)
      ? candidate.motion
      : DEFAULT_ACCESSIBILITY_SETTINGS.motion,
    underlineLinks:
      typeof candidate.underlineLinks === 'boolean'
        ? candidate.underlineLinks
        : DEFAULT_ACCESSIBILITY_SETTINGS.underlineLinks,
    dyslexiaFont:
      typeof candidate.dyslexiaFont === 'boolean'
        ? candidate.dyslexiaFont
        : DEFAULT_ACCESSIBILITY_SETTINGS.dyslexiaFont,
  }
}

export function applyAccessibilitySettings(settings: AccessibilitySettings) {
  if (typeof document === 'undefined') return
  const html = document.documentElement
  html.setAttribute('data-a11y-text-size', settings.textSize)
  html.setAttribute('data-a11y-contrast', settings.contrast)
  html.setAttribute('data-a11y-motion', settings.motion)
  html.setAttribute('data-a11y-underline-links', settings.underlineLinks ? 'true' : 'false')
  html.setAttribute('data-a11y-dyslexia', settings.dyslexiaFont ? 'true' : 'false')
}

function readStoredSettings(): AccessibilitySettings {
  if (typeof window === 'undefined') return DEFAULT_ACCESSIBILITY_SETTINGS
  try {
    const raw = window.localStorage.getItem(ACCESSIBILITY_STORAGE_KEY)
    if (!raw) return DEFAULT_ACCESSIBILITY_SETTINGS
    return parseAccessibilitySettings(JSON.parse(raw))
  } catch {
    return DEFAULT_ACCESSIBILITY_SETTINGS
  }
}

function persistSettings(settings: AccessibilitySettings) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(ACCESSIBILITY_STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // Ignore quota / privacy-mode failures; in-memory state still works.
  }
}

export type UseAccessibilitySettingsResult = {
  settings: AccessibilitySettings
  ready: boolean
  update: <K extends keyof AccessibilitySettings>(key: K, value: AccessibilitySettings[K]) => void
  reset: () => void
}

export function useAccessibilitySettings(): UseAccessibilitySettingsResult {
  const [settings, setSettings] = useState<AccessibilitySettings>(DEFAULT_ACCESSIBILITY_SETTINGS)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const stored = readStoredSettings()
    setSettings(stored)
    applyAccessibilitySettings(stored)
    setReady(true)
  }, [])

  const update = useCallback<UseAccessibilitySettingsResult['update']>((key, value) => {
    setSettings((current) => {
      const next = { ...current, [key]: value }
      applyAccessibilitySettings(next)
      persistSettings(next)
      return next
    })
  }, [])

  const reset = useCallback(() => {
    setSettings(DEFAULT_ACCESSIBILITY_SETTINGS)
    applyAccessibilitySettings(DEFAULT_ACCESSIBILITY_SETTINGS)
    persistSettings(DEFAULT_ACCESSIBILITY_SETTINGS)
  }, [])

  return { settings, ready, update, reset }
}
